import { setOptions } from '@googlemaps/js-api-loader'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import styled from 'styled-components/macro'

import { MIN_ZOOM, VISIBLE_CLUSTER_ZOOM_LIMIT } from '../../constants/map'
import { LabelVisibility, MapType, OverlayType } from '../../constants/settings'
import { fetchFilterCounts } from '../../redux/filterSlice'
import { setFromSettings } from '../../redux/locationSlice'
import { fetchLocations } from '../../redux/viewChange'
import { updateLastMapView } from '../../redux/viewportSlice'
import { viewToString } from '../../utils/appUrl'
import throttle from '../../utils/throttle'
import { useAppHistory } from '../../utils/useAppHistory'
import { useIsEmbed } from '../../utils/useBreakpoint'
import Share from '../share/Share'
import ShareIconButton from '../share/ShareIconButton'
import { AddLocationMobile } from '../ui/AddLocation'
import LoadingIndicator from '../ui/LoadingIndicator'
import CloseStreetView from './CloseStreetView'
import ClusterMarkers from './ClusterMarkers'
import GeolocationDot from './GeolocationDot'
import LocationMarkers from './LocationMarkers'
import PanoramaEvents from './PanoramaEvents'
import PinMarkers from './PinMarkers'
import Place from './Place'
import TrackLocationButton from './TrackLocationButton'
import useCreateGoogleMap from './useCreateGoogleMap'

let googleMapsLoaderConfigured = false
const configureGoogleMapsLoader = (language) => {
  if (googleMapsLoaderConfigured) {
    return
  }
  setOptions({
    key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    v: 'quarterly',
    libraries: ['places', 'geometry'],
    language,
    region: 'CH',
  })
  googleMapsLoaderConfigured = true
}

const BottomLeftLoadingIndicator = styled(LoadingIndicator)`
  position: absolute;
  inset-inline-start: 10px;
  inset-block-end: 10px;
`

const ZoomButton = styled.button`
  position: absolute;
  inset-inline-start: 10px;
  width: 40px;
  height: 40px;
  background-color: white;
  color: ${({ theme }) => theme.headerText};
  ${({ isDesktop }) =>
    isDesktop &&
    `
    &:hover {
      background-color: #f0f0f0;
    }
  `}
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.5 : 1)};
  z-index: 1;
  user-select: none;
`

const StyledIconButton = styled(ShareIconButton)`
  background-color: white;
  border-radius: 4px;
  border: 1px solid #ccc;
  ${({ isDesktop }) =>
    isDesktop &&
    `
    &:hover {
      background-color: #f0f0f0;
    }
  `}
  svg {
    color: black;
  }
`

const ZoomInButton = styled(ZoomButton)`
  inset-block-start: calc(50% - 45px);
`

const ZoomOutButton = styled(ZoomButton)`
  inset-block-start: calc(50% + 5px);
`

const ShareContainer = styled.div`
  position: absolute;
  inset-block-start: 10px;
  inset-inline-end: 10px;
  background-color: white;
  border-radius: 4px;
  padding: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  z-index: 1;
  width: 300px;
`

const MapContainer = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`

const EARTH_RADIUS = 6378137
const EARTH_CIRCUMFERENCE = 2 * Math.PI * EARTH_RADIUS

const clusterBounds = ({ lat, lng, zoom }) => {
  const mercator = {
    x: (lng / 360) * EARTH_CIRCUMFERENCE,
    y: Math.log(Math.tan((lat + 90) * (Math.PI / 360))) * EARTH_RADIUS,
  }

  const cell_size = EARTH_CIRCUMFERENCE / 2 ** zoom
  const cell = {
    x: Math.floor((mercator.x + EARTH_CIRCUMFERENCE / 2) / cell_size),
    y: Math.floor((mercator.y + EARTH_CIRCUMFERENCE / 2) / cell_size),
  }

  const bounds = {
    south: cell.y * cell_size - EARTH_CIRCUMFERENCE / 2,
    west: cell.x * cell_size - EARTH_CIRCUMFERENCE / 2,
    north: (cell.y + 1) * cell_size - EARTH_CIRCUMFERENCE / 2,
    east: (cell.x + 1) * cell_size - EARTH_CIRCUMFERENCE / 2,
  }

  return {
    south:
      90 -
      (Math.atan2(1, Math.exp(bounds.south / EARTH_RADIUS)) * 360) / Math.PI,
    west: bounds.west * (360 / EARTH_CIRCUMFERENCE),
    north:
      90 -
      (Math.atan2(1, Math.exp(bounds.north / EARTH_RADIUS)) * 360) / Math.PI,
    east: bounds.east * (360 / EARTH_CIRCUMFERENCE),
  }
}

// ~1e-6 degrees ≈ 0.1 m
const VIEW_EPSILON = 1e-6

const boundsAreEqual = (a, b) =>
  Math.abs(a.north - b.north) < VIEW_EPSILON &&
  Math.abs(a.south - b.south) < VIEW_EPSILON &&
  Math.abs(a.east - b.east) < VIEW_EPSILON &&
  Math.abs(a.west - b.west) < VIEW_EPSILON

const boundsAreEmpty = (bounds) =>
  Math.abs(bounds.north - bounds.south) < VIEW_EPSILON &&
  Math.abs(bounds.east - bounds.west) < VIEW_EPSILON

const shouldFetchForView = (previousView, nextView) => {
  if (boundsAreEmpty(nextView.bounds)) {
    return false
  }
  if (!previousView) {
    return true
  }
  return (
    previousView.zoom !== nextView.zoom ||
    previousView.width !== nextView.width ||
    previousView.height !== nextView.height ||
    !boundsAreEqual(previousView.bounds, nextView.bounds)
  )
}

const makeHandleViewChange = (dispatch, googleMap, history) => {
  let lastFetchedView = null

  const throttledDispatches = throttle((newView) => {
    dispatch(updateLastMapView(newView))
    dispatch(fetchLocations())
    dispatch(fetchFilterCounts())
  }, 1000)

  return () => {
    const center = googleMap.getCenter()
    const newView = {
      center: { lat: center.lat(), lng: center.lng() },
      zoom: googleMap.getZoom(),
      bounds: googleMap.getBounds().toJSON(),
      width: googleMap.getDiv().offsetWidth,
      height: googleMap.getDiv().offsetHeight,
    }

    history.syncViewToBrowserUrl(newView)

    if (!shouldFetchForView(lastFetchedView, newView)) {
      return
    }
    lastFetchedView = newView
    throttledDispatches(newView)
  }
}

const buildMapStyles = (showBusinesses) => [
  {
    featureType: 'poi',
    elementType: 'labels.icon',
    stylers: [{ visibility: showBusinesses ? 'on' : 'off' }],
  },
  {
    featureType: 'landscape',
    elementType: 'labels.icon',
    stylers: [{ visibility: showBusinesses ? 'on' : 'off' }],
  },
]

const createOverlayLayers = (maps, map, layerTypes) =>
  layerTypes
    .map((name) => {
      const LayerClass = maps[name]
      if (!LayerClass) {
        return null
      }
      const layer = new LayerClass()
      layer.setMap(map)
      return layer
    })
    .filter(Boolean)

const configurePanoramaControls = (googleMap, showPegman) => {
  googleMap.setOptions({ streetViewControl: showPegman })

  const panorama = googleMap.getStreetView()
  if (panorama) {
    panorama.setOptions({
      fullscreenControl: false,
      enableCloseButton: false,
      addressControl: false,
      motionTracking: false,
      motionTrackingControl: true,
      zoomControl: false,
    })
  }
}

const filterClustersAroundSelectedLocation = (clusters, selectedLocation) =>
  clusters.filter((cluster) => {
    if (
      selectedLocation &&
      cluster.count === 1 &&
      Math.abs(cluster.lat - selectedLocation.lat) < 1e-6 &&
      Math.abs(cluster.lng - selectedLocation.lng) < 1e-6
    ) {
      return false
    }
    return true
  })

const getVisibleLocations = (
  panoramaReady,
  panoramaLocations,
  clusters,
  locations,
  selectedLocation,
) => {
  if (panoramaReady) {
    return panoramaLocations
  }
  if (clusters.length !== 0) {
    return selectedLocation ? [selectedLocation] : []
  }
  if (selectedLocation) {
    return [...locations, selectedLocation].filter(
      (loc, index, self) => index === self.findIndex((t) => t.id === loc.id),
    )
  }
  return locations
}

const MapPage = ({ isDesktop }) => {
  const { i18n } = useTranslation()
  const isRTL = i18n.dir() === 'rtl'

  configureGoogleMapsLoader(i18n.language)
  const history = useAppHistory()
  const dispatch = useDispatch()
  const idleListenerRef = useRef(null)
  const mapClickListenerRef = useRef(null)
  const mapContainerRef = useRef(null)
  const overlayLayersRef = useRef([])
  const initListenerRef = useRef(null)
  const prevIsLoggedInRef = useRef(null)

  const [shareOpen, setShareOpen] = useState(false)

  const {
    initialView,
    locations,
    clusters,
    isLoading: mapIsLoading,
    googleMap,
    getGoogleMaps,
    geometryReady,
  } = useSelector((state) => state.map)

  const isLoggedIn = useSelector((state) => !!state.auth.user)

  const currentZoom = googleMap?.getZoom()

  const {
    panoramaReady,
    streetViewOpen,
    locations: panoramaLocations,
  } = useSelector((state) => state.panorama)

  const place = useSelector((state) => state.place.selectedPlace)

  const { geolocation } = useSelector((state) => state.geolocation)
  const { pathname, search } = useLocation()
  const {
    locationId,
    isBeingEdited: isEditingLocation,
    location: selectedLocationRedux,
    isLoading: locationIsLoading,
    isBeingInitializedMobile,
  } = useSelector((state) => state.location)
  const { mapType, overlay, labelVisibility, showBusinesses } = useSelector(
    (state) => state.settings,
  )

  const selectedLocation =
    locations.find((l) => l.id === locationId) || selectedLocationRedux

  const layerTypes = overlay
    ? [OverlayType.toLayerType(overlay)].filter(Boolean)
    : []

  const { typesAccess, isLoading: typesAreLoading } = useSelector(
    (state) => state.type,
  )

  useCreateGoogleMap({
    initialView,
    googleMap,
    mapType,
    mapContainerRef,
    initListenerRef,
    idleListenerRef,
    overlayLayersRef,
  })

  const searchParams = new URLSearchParams(search)
  const hasTypesParams =
    searchParams.has('types') || searchParams.has('f') || searchParams.has('c')

  const [isMapVisible, setIsMapVisible] = useState(false)

  useEffect(() => {
    const container = mapContainerRef.current
    if (!container) {
      return undefined
    }

    const updateVisibility = () => {
      setIsMapVisible(container.offsetWidth > 0 && container.offsetHeight > 0)
    }

    updateVisibility()

    const observer = new ResizeObserver(updateVisibility)
    observer.observe(container)

    return () => observer.disconnect()
  }, [googleMap])

  useEffect(() => {
    if (!googleMap || !getGoogleMaps || !isMapVisible) {
      return
    }
    const maps = getGoogleMaps()
    googleMap.setOptions({
      mapTypeId: mapType,
      streetViewControlOptions: {
        position: isRTL
          ? maps.ControlPosition.RIGHT_BOTTOM
          : maps.ControlPosition.LEFT_BOTTOM,
      },
      rotateControlOptions: {
        position: isRTL
          ? maps.ControlPosition.RIGHT_BOTTOM
          : maps.ControlPosition.LEFT_BOTTOM,
      },
      styles: buildMapStyles(showBusinesses),
    })
  }, [googleMap, getGoogleMaps, isMapVisible, mapType, showBusinesses, isRTL])

  useEffect(() => {
    if (!googleMap || !getGoogleMaps) {
      return
    }
    const maps = getGoogleMaps()
    overlayLayersRef.current.forEach((layer) => layer.setMap(null))
    overlayLayersRef.current = createOverlayLayers(maps, googleMap, layerTypes)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleMap, getGoogleMaps, layerTypes.join(',')])

  useEffect(() => {
    const ready =
      dispatch && !typesAccess.isNull && googleMap && !hasTypesParams
    if (!ready) {
      return
    }

    const handleViewChange = makeHandleViewChange(dispatch, googleMap, history)
    const google = getGoogleMaps()

    if (idleListenerRef.current) {
      google.event.removeListener(idleListenerRef.current)
    }
    idleListenerRef.current = google.event.addListener(
      googleMap,
      'idle',
      handleViewChange,
    )

    handleViewChange()
  }, [!typesAccess.isNull, googleMap, !!dispatch, hasTypesParams]) //eslint-disable-line

  // fetchLocations has in_list when logged in
  useEffect(() => {
    const prevIsLoggedIn = prevIsLoggedInRef.current
    prevIsLoggedInRef.current = isLoggedIn

    if (prevIsLoggedIn === null) {
      return
    }
    if (prevIsLoggedIn === isLoggedIn) {
      return
    }
    if (!googleMap || !getGoogleMaps) {
      return
    }
    dispatch(fetchLocations())
  }, [isLoggedIn, googleMap, getGoogleMaps, dispatch])

  const allClusters = filterClustersAroundSelectedLocation(
    clusters,
    selectedLocation,
  )
  const allLocations = getVisibleLocations(
    panoramaReady,
    panoramaLocations,
    clusters,
    locations,
    selectedLocation,
  )

  const isAddingLocation = locationId === 'new' || isBeingInitializedMobile
  const isViewingLocation =
    locationId !== null && !isEditingLocation && !isAddingLocation

  const showLabels =
    isAddingLocation ||
    isEditingLocation ||
    (panoramaReady
      ? labelVisibility !== LabelVisibility.Off
      : LabelVisibility.shouldShowLabels(labelVisibility, currentZoom))

  useEffect(() => {
    if (!googleMap || !getGoogleMaps) {
      return
    }
    const zoom = googleMap.getZoom()
    const zoomOk = zoom == null || zoom > VISIBLE_CLUSTER_ZOOM_LIMIT
    const showPegman = zoomOk && geometryReady
    configurePanoramaControls(googleMap, showPegman)
  }, [googleMap, getGoogleMaps, currentZoom, geometryReady])

  const isEmbed = useIsEmbed()

  const handleClusterClick = (cluster) => {
    if (cluster.count === 1) {
      googleMap?.panTo({
        lat: cluster.lat,
        lng: cluster.lng,
      })
      googleMap?.setZoom(VISIBLE_CLUSTER_ZOOM_LIMIT + 1)
    } else {
      const bounds = clusterBounds({
        lat: cluster.lat,
        lng: cluster.lng,
        zoom: currentZoom + 1,
      })
      googleMap?.fitBounds(bounds)
    }
    if (isViewingLocation) {
      const center = googleMap.getCenter()
      const zoom = googleMap.getZoom()
      const viewString = viewToString(center.lat(), center.lng(), zoom)
      history.push(`/map/${viewString}`)
    }
  }

  const handleLocationClick = useCallback(
    (location) => {
      if (isEditingLocation || isAddingLocation) {
        return
      }
      if (isDesktop && pathname.includes('/settings')) {
        dispatch(setFromSettings(true))
      }
      history.push(`/locations/${location.id}?pane=&tab=`)
    },
    [
      isDesktop,
      pathname,
      dispatch,
      history,
      isEditingLocation,
      isAddingLocation,
    ],
  )

  useEffect(() => {
    if (!googleMap || !getGoogleMaps) {
      return
    }

    const google = getGoogleMaps()

    if (mapClickListenerRef.current) {
      google.event.removeListener(mapClickListenerRef.current)
    }

    mapClickListenerRef.current = google.event.addListener(
      googleMap,
      'click',
      () => {
        if (isViewingLocation) {
          history.push('/map?pane=&tab=')
        }
      },
    )

    return () => {
      if (mapClickListenerRef.current) {
        google.event.removeListener(mapClickListenerRef.current)
        mapClickListenerRef.current = null
      }
    }
  }, [googleMap, getGoogleMaps, isViewingLocation, history])

  const zoomIn = () => {
    if (googleMap) {
      googleMap.setZoom(googleMap.getZoom() + 1)
    }
  }
  const zoomOut = () => {
    if (googleMap) {
      googleMap.setZoom(googleMap.getZoom() - 1)
    }
  }

  const toggleShare = useCallback(() => {
    setShareOpen((prev) => !prev)
  }, [])

  return (
    <>
      {(mapIsLoading || locationIsLoading || typesAreLoading) && (
        <BottomLeftLoadingIndicator />
      )}
      {!isAddingLocation && !isEditingLocation && !isDesktop && !isEmbed && (
        <AddLocationMobile />
      )}
      {!isDesktop && !isEmbed && (
        <TrackLocationButton isIcon disabled={!googleMap} />
      )}

      <ZoomInButton
        className="map-zoom-button"
        onClick={zoomIn}
        disabled={
          !googleMap ||
          !currentZoom ||
          currentZoom >= MapType.getMaxZoom(mapType)
        }
        isDesktop={isDesktop}
      >
        +
      </ZoomInButton>
      <ZoomOutButton
        className="map-zoom-button"
        onClick={zoomOut}
        disabled={!googleMap || !currentZoom || currentZoom <= MIN_ZOOM}
        isDesktop={isDesktop}
      >
        -
      </ZoomOutButton>

      {isDesktop && (
        <>
          {shareOpen ? (
            <ShareContainer>
              <Share onClose={() => setShareOpen(false)} />
            </ShareContainer>
          ) : (
            <div
              style={{
                position: 'absolute',
                insetBlockStart: '10px',
                insetInlineEnd: '10px',
                zIndex: 1,
              }}
            >
              <StyledIconButton size={40} onClick={toggleShare} />
            </div>
          )}
        </>
      )}

      {googleMap && <PanoramaEvents />}
      {panoramaReady && <CloseStreetView />}

      {googleMap && getGoogleMaps && (
        <PinMarkers
          googleMap={googleMap}
          getGoogleMaps={getGoogleMaps}
          selectedLocationLat={selectedLocation?.lat}
          selectedLocationLng={selectedLocation?.lng}
          isEditing={isEditingLocation}
          isAdding={isAddingLocation}
          streetViewOpen={streetViewOpen}
        />
      )}

      {initialView && <MapContainer ref={mapContainerRef} />}

      {googleMap && getGoogleMaps && (
        <>
          {geolocation && !geolocation.loading && !geolocation.error && (
            <GeolocationDot />
          )}
          {place &&
            place.location &&
            place.view &&
            place.view.zoom >= VISIBLE_CLUSTER_ZOOM_LIMIT &&
            currentZoom >= VISIBLE_CLUSTER_ZOOM_LIMIT && (
              <Place
                lat={place.location.lat}
                lng={place.location.lng}
                label={place.location.description}
              />
            )}
          <ClusterMarkers
            clusters={allClusters}
            googleMap={googleMap}
            getGoogleMaps={getGoogleMaps}
            onClusterClick={handleClusterClick}
          />
          <LocationMarkers
            locations={allLocations}
            googleMap={googleMap}
            getGoogleMaps={getGoogleMaps}
            onLocationClick={handleLocationClick}
            showLabels={showLabels}
          />
        </>
      )}
    </>
  )
}

export default MapPage
